import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import { PROSPECT_TYPE } from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import {
  GetCandidateListParams,
  GetProspectListParams,
} from '@nxsys/store/slices/prospect/types';
import { Dropdown } from 'antd';
import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import FilterModal from './FilterModal';

type ActionsHeaderProps = {
  type: PROSPECT_TYPE;
  onAddNew?: () => void;
  fetchData: (params: GetCandidateListParams) => void;
  onProgressOnboardingSelected?: () => void;
  onProgressRegistrationSelected?: () => void;
  onDeleteCandidateSelected?: () => void;
  onResendByIdsSelected?: () => void;
  onDeleteProspectSelected?: () => void;
  onFilter?: (
    params: GetProspectListParams & GetCandidateListParams,
  ) => void;
};

export const ActionsHeader = ({
  type,
  onAddNew,
  fetchData,
  onProgressOnboardingSelected,
  onProgressRegistrationSelected,
  onDeleteCandidateSelected,
  onResendByIdsSelected,
  onDeleteProspectSelected,
  onFilter,
}: ActionsHeaderProps) => {
  const { messages } = useTypedIntl();

  const { isAgency } = useSelector((state: AppState) => state.appStore);

  const { candidateList, agencyList, prospectList } = useSelector(
    (state: AppState) => state.prospectStore,
  );

  const [searchValue, setSearchValue] = useState('');

  const [openFilter, setOpenFilter] = useState(false);

  const toggleFilter = () => {
    setOpenFilter((prev) => !prev);
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      fetchData({
        ...(type === PROSPECT_TYPE.CANDIDATE ? candidateList.queryParams : {}),
        ...(type === PROSPECT_TYPE.AGENCY ? agencyList.queryParams : {}),
        ...(type === PROSPECT_TYPE.ONBOARDING ? prospectList.queryParams : {}),
        page: 0,
        search,
      });
    }, 500),
    [candidateList.queryParams, agencyList.queryParams, prospectList.queryParams, fetchData, type],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const renderButton = () => {
    if (isAgency) {
      if (onAddNew) {
        return (
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            onClick={() => onAddNew?.()}
            leftIcon={<PlusOutlined />}
          />
        );
      }

      return null;
    }

    return (
      <Dropdown menu={menuProps}>
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.bulkAction']}
          rightIcon={<DownOutlined />}
        />
      </Dropdown>
    );
  };

  const items = [
    {
      key: 'progressToRegistration',
      label: messages['prospect.candidate.progressToRegistration'],
      onClick: onProgressRegistrationSelected,
    },
    ...(type === PROSPECT_TYPE.CANDIDATE
      ? [
          {
            key: 'progressToOnboarding',
            label: messages['prospect.candidate.progressToOnboarding'],
            onClick: onProgressOnboardingSelected,
          },
          {
            key: 'delete',
            label: messages['common.delete'],
            onClick: onDeleteCandidateSelected,
          },
        ]
      : []),
    ...(type === PROSPECT_TYPE.ONBOARDING
      ? [
          {
            key: 'resendRegistration',
            label: messages['prospect.onboarding.resendRegistration'],
            onClick: onResendByIdsSelected,
          },
          {
            key: 'delete',
            label: messages['common.delete'],
            onClick: onDeleteProspectSelected,
          },
        ]
      : []),
  ];

  const menuProps = {
    items,
  };

  // Agency prospects hold no employer, agency or payment model to filter on
  const showFilter = type !== PROSPECT_TYPE.AGENCY && !!onFilter;

  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <div className="flex items-center gap-2">
        <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        {showFilter && (
          <AppMenuFilter
            options={[]}
            onChange={() => {}}
            onClick={toggleFilter}
          />
        )}
      </div>
      {renderButton()}
      {showFilter && (
        <FilterModal
          isOpen={openFilter}
          toggle={toggleFilter}
          type={type}
          title={messages['common.filter']}
          onFilter={onFilter}
        />
      )}
    </div>
  );
};
